import { useArrowSource } from '@components/ArtBoard/Arrows/hooks/useArrowSource'
import type { ArrowUI } from '@components/ArtBoard/Arrows/types'
import SmartArrow from '@lib/components/SmartArrow'
import { useClickOutside } from '@lib/hooks'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { CanvasActionTypes } from '@storeOld/reducers/canvas/canvas'
import React, { useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'

import { NODE_GHOST } from '@/constants/constants'
import { isSafari } from '@/helpers'
import { useScaleZoom } from '@/hooks'
import { clsJoin } from '@/utils'
import {
  ArrowOrientation,
  ArrowType,
  getArrowClassification,
  getMiddlePoint,
} from '@/utils/arrows'
import css from './styles.module.css'

export const ArrowSmooth: React.FC<ArrowUI> = ({
  colorId,
  id,
  visible,
  relation,
  model,
  opacityState,
  isSelected,
  isHighlighted,
}) => {
  const { color, nodeVariant: nodeStartVariant } = useArrowSource(
    relation[0],
    colorId
  )

  const dispatch = useDispatch()

  const { scale } = useScaleZoom()

  const isReadOnly = useSelector(
    migrationProjectSelector.getProjectLegacyAccessTypeIsReadOnly
  )

  const [isArrowVisible, setIsArrowVisible] = useState(visible)

  const onClickArrow = () => {
    if (isReadOnly) {
      return
    }

    dispatch({
      type: CanvasActionTypes.SET_SELECTED_ARROW_ID,
      payload: {
        id,
      },
    })
  }

  const clickOutside = useClickOutside<HTMLDivElement>(
    isSelected as boolean,
    () => {},
    onClickArrow
  )

  const zIndex = isSelected ? 999 : 0
  const options = {
    strokeWidth: 2 * scale,
    headSize: 5,
    headColor: color,
    lineColor: color,
    dashness: !isSafari && !isSelected && !isHighlighted,
  }

  const style = React.useMemo(
    () => ({
      '--arrow-color': color,
      zIndex,
    }),
    [color, zIndex]
  )

  // Hack to prevent displaying arrows if:
  // 1. Section has been deleted
  // 2. Page with arrows has been moved to collapsed element
  // @TODO find a way how to resolve it with redux
  //
  const { variant: nodeEndVariant } = useSelector(
    migrationProjectSelector.getProjectNodeById(relation[1])
  )

  // hack
  // @TODO find a way how to remove it
  const [rerenderId, setRerenderId] = useState<number>(Math.random)

  useLayoutEffect(() => {
    if (!isSelected) {
      const startElement = document.getElementById(relation[0])
      const endElement = document.getElementById(relation[1])

      if (!startElement || !endElement) {
        setIsArrowVisible(false)
      } else {
        setTimeout(() => {
          setRerenderId(Math.random)
        }, 100)
      }
    }
  }, [])

  if (
    !isArrowVisible ||
    nodeEndVariant === NODE_GHOST ||
    nodeStartVariant === NODE_GHOST
  ) {
    return null
  }

  const nodeStart = document.getElementById(relation[0])
  const nodeEnd = document.getElementById(relation[1])

  if (!nodeStart || !nodeEnd) {
    return null
  }

  const { arrowType, arrowOrientation } = getArrowClassification(
    nodeStart,
    nodeEnd,
    scale
  )

  if (arrowType === ArrowType.MIDDLE) {
    const middlePoint = getMiddlePoint(
      nodeStart,
      nodeEnd,
      arrowOrientation,
      scale
    )
    const middlePointId = `${rerenderId}-middle-point`

    return (
      <div
        className={clsJoin(
          css.container,
          'Arrow-smooth',
          `Arrow-model-${model}`
        )}
        data-arrow-id={id}
        style={style}
        {...clickOutside}
      >
        <div
          className={clsJoin(css.arrow, css[opacityState])}
          data-id={rerenderId}
        >
          {createPortal(
            <div
              id={middlePointId}
              className={css.middlePoint}
              style={{
                left: middlePoint.left,
                top: middlePoint.top,
              }}
            />,
            document.body
          )}
          <SmartArrow
            {...options}
            showHead={false}
            start={relation[0]}
            end={middlePointId}
            startAnchor={
              arrowOrientation === ArrowOrientation.RIGHT ? 'right' : 'left'
            }
            endAnchor="top"
            labels={
              isSelected ? (
                <div
                  data-arrow-toolbar-anchor={id}
                  style={{ position: 'absolute' }}
                />
              ) : null
            }
          />
          <SmartArrow
            {...options}
            start={middlePointId}
            end={relation[1]}
            startAnchor="bottom"
            endAnchor={
              arrowOrientation === ArrowOrientation.RIGHT ? 'right' : 'left'
            }
          />
        </div>
      </div>
    )
  }

  return (
    <div
      className={clsJoin(css.container, 'Arrow-smooth', `Arrow-model-${model}`)}
      data-arrow-id={id}
      style={style}
      {...clickOutside}
    >
      <div
        className={clsJoin(css.arrow, css[opacityState])}
        data-id={rerenderId}
      >
        <SmartArrow
          {...options}
          start={relation[0]}
          end={relation[1]}
          startAnchor={
            arrowOrientation === ArrowOrientation.RIGHT ? 'right' : 'left'
          }
          endAnchor={
            arrowOrientation === ArrowOrientation.RIGHT ? 'left' : 'right'
          }
          labels={
            isSelected ? (
              <div
                data-arrow-toolbar-anchor={id}
                style={{ position: 'absolute' }}
              />
            ) : null
          }
        />
      </div>
    </div>
  )
}
